import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ShoppingCart, Check, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DateInput from "@/components/DateInput";
import ProjectSelect from "@/components/ProjectSelect";
import { fmtDate, todayISO } from "@/lib/utils";

const EMPTY = { project_id: "", request_date: todayISO(), material_name: "", unit: "adet", quantity: 0, needed_by: "", priority: "normal", status: "beklemede", requester: "", notes: "" };
const UNITS = ["m", "m2", "m3", "adet", "kg", "ton", "lt", "takım"];
const PRIORITY = [{v: "dusuk", l: "Düşük"}, {v: "normal", l: "Normal"}, {v: "yuksek", l: "Yüksek"}, {v: "acil", l: "Acil"}];

export default function MaterialRequests() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/material-requests${filter ? `?project_id=${filter}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.project_id || !form.material_name) { toast.error("Proje ve malzeme zorunlu"); return; }
    const payload = { ...form, quantity: Number(form.quantity) };
    if (!payload.needed_by) delete payload.needed_by;
    await api.post("/material-requests", payload);
    toast.success("Malzeme talebi oluşturuldu"); setOpen(false); setForm(EMPTY); load();
  };
  const remove = async (id) => { await api.delete(`/material-requests/${id}`); toast.success("Silindi"); load(); };
  const setStatus = async (id, status) => {
    await api.put(`/material-requests/${id}/status?status=${status}`);
    toast.success("Durum güncellendi"); load();
  };

  const statusColor = { beklemede: "bg-amber-500/15 text-amber-700 dark:text-amber-400", onaylandi: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", reddedildi: "bg-rose-500/15 text-rose-700 dark:text-rose-400", teslim_edildi: "bg-blue-500/15 text-blue-700 dark:text-blue-400" };
  const priorityColor = { dusuk: "bg-slate-500/15 text-slate-700 dark:text-slate-300", normal: "bg-blue-500/15 text-blue-700 dark:text-blue-400", yuksek: "bg-amber-500/15 text-amber-700 dark:text-amber-400", acil: "bg-rose-500/15 text-rose-700 dark:text-rose-400" };

  return (
    <div data-testid="material-requests-page">
      <PageHeader title="Malzeme Talepleri" subtitle="Satın alma ve stok talepleri.">
        <ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-mr-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni Talep</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Yeni Malzeme Talebi</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" /></div>
              <div><Label>Talep Tarihi</Label><DateInput data-testid="input-request-date" value={form.request_date} onChange={e => setForm({...form, request_date: e.target.value})} /></div>
              <div><Label>İhtiyaç Tarihi</Label><DateInput data-testid="input-needed-by" value={form.needed_by} onChange={e => setForm({...form, needed_by: e.target.value})} /></div>
              <div className="col-span-2"><Label>Malzeme *</Label><Input data-testid="input-material" value={form.material_name} onChange={e => setForm({...form, material_name: e.target.value})} /></div>
              <div><Label>Birim</Label><Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Miktar</Label><Input type="number" step="0.01" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} /></div>
              <div><Label>Öncelik</Label><Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITY.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Talep Eden</Label><Input value={form.requester} onChange={e => setForm({...form, requester: e.target.value})} /></div>
              <div className="col-span-2"><Label>Notlar</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-mr-button">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <ShoppingCart className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz malzeme talebi yok</div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>Tarih</TableHead><TableHead>Malzeme</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Öncelik</TableHead><TableHead>Talep Eden</TableHead><TableHead>Durum</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono-nums">{fmtDate(r.request_date)}</TableCell>
                  <TableCell className="font-medium">{r.material_name}</TableCell>
                  <TableCell className="text-right font-mono-nums">{r.quantity} {r.unit}</TableCell>
                  <TableCell><Badge className={priorityColor[r.priority]}>{r.priority}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{r.requester || "-"}</TableCell>
                  <TableCell><Badge className={statusColor[r.status]}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {r.status === "beklemede" && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => setStatus(r.id, "onaylandi")} data-testid={`approve-${r.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setStatus(r.id, "reddedildi")}><X className="w-4 h-4 text-rose-500" /></Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
